import L, { type BaseIconOptions, type Map, geoJSON, type Layer } from 'leaflet'
import { Icon, LatLng, Marker } from 'leaflet'
import { useEffect, useState } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router'
import Supercluster, { type PointFeature, type AnyProps } from 'supercluster'
import usePromise, { isFulfilled } from '../../../../shared/hooks/usePromise'
import { fetchWithToken } from '../../../../shared/utils/api/api'
import { createFiltersObject } from '../../../../shared/utils/dataSelectionLegacy/normalizations'
import useLegacyDataselectionConfig from '../../../../shared/components/DataSelectionLegacy/useLegacyDataselectionConfig'
import useBuildQueryString from '../../../../shared/hooks/useBuildQueryString'
import useParam from '../../../../shared/hooks/useParam'
import config, {
  DataSelectionMapVisualizationType,
  DataSelectionType,
  DETAIL_ICON,
} from '../../config'
import { useMapContext } from '../../../../shared/contexts/map/MapContext'
import { polygonParam } from '../../query-params'
import { normalizeMapVisualization, type MapVisualizationMarkers } from './normalize'
import type { BuildQueryString } from '../../../../shared/hooks/useBuildQueryString/useBuildQueryString'
import useDataSelection from '../../../../shared/contexts/DataSelection/useDataSelection'

export const CLUSTER_ICON_CLASS = 'cluster-icon'
export const SELECTION_DETAIL_CLASS = 'dataselection-detail-marker'

interface Configs {
  [key: string]: {
    requestFocus?: boolean
    style: {
      color: string
      fillOpacity: number
      weight: number
      dashArray?: string
      opacity?: number
    }
  }
}
const geoJsonConfig: Configs = {
  dataSelection: {
    style: {
      color: '#ec0000',
      fillOpacity: 0.33,
      weight: 1,
    },
  },
  dataSelectionAlternate: {
    style: {
      color: '#ec0000',
      dashArray: '3 6',
      fillOpacity: 0.17,
      weight: 2,
    },
  },
  dataSelectionBounds: {
    style: {
      color: '#ec0000',
      fillOpacity: 0,
      opacity: 0,
      weight: 1,
    },
    requestFocus: true,
  },
}

const useVisualizationData = (datasetType: DataSelectionType) => {
  const [polygon] = useParam(polygonParam)
  const { activeFilters } = useDataSelection()

  return usePromise(async () => {
    if (!polygon && !activeFilters.length) {
      return null
    }
    const searchParams = new URLSearchParams({
      shape: JSON.stringify(polygon?.polygon.map(({ lat, lng }) => [lng, lat])),
      ...createFiltersObject(activeFilters),
    })
    const {
      object_list: data,
      eigenpercelen: eigenPercelen,
      niet_eigenpercelen: nietEigenPercelen,
      extent,
    } = await fetchWithToken(
      `${config[datasetType]?.endpointMapVisualization ?? ''}?${searchParams.toString()}`,
    )
    return normalizeMapVisualization(datasetType, data, {
      eigenPercelen,
      nietEigenPercelen,
      extent,
    })
  }, [polygon, activeFilters])
}

const getMarkers = (
  visualizationData: MapVisualizationMarkers,
  navigate: NavigateFunction,
  buildQueryString: BuildQueryString,
  datasetType: DataSelectionType,
) =>
  visualizationData.data.map(({ latLng, id }) => {
    const lat = latLng[0]
    const lng = latLng[1]
    const marker = new Marker(new LatLng(lat, lng), {
      icon: new Icon({
        ...DETAIL_ICON,
        className: SELECTION_DETAIL_CLASS,
      } as BaseIconOptions),
    })
    marker.on('click', () => {
      const pathname = config[datasetType].getDetailPath(id)
      const queryString = buildQueryString(undefined, [polygonParam])
      navigate(`${pathname}?${queryString}`)
    })

    return marker
  })

const getClusterIndex = (markers: Marker[]) => {
  const points = markers.map((marker, index) => {
    const latLng = marker.getLatLng()

    return {
      type: 'Feature',
      properties: { id: index },
      geometry: {
        type: 'Point',
        coordinates: [latLng.lng, latLng.lat],
      },
    } as PointFeature<AnyProps>
  })

  const clusterIndex = new Supercluster({
    log: false,
    radius: 30,
    extent: 3000,
    maxZoom: 13,
  })
  clusterIndex.load(points)

  return clusterIndex
}

const getClustersOrMarkers = (map: Map, clusterIndex: Supercluster, markers: Layer[]) => {
  const bounds = map.getBounds()

  const clusters = clusterIndex.getClusters(
    [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
    map.getZoom(),
  )

  return clusters.map((clusterOrMarker) => {
    const [longitude, latitude] = clusterOrMarker.geometry.coordinates
    const { id, cluster, point_count: pointCount } = clusterOrMarker.properties

    if (cluster) {
      const clusterIcon = L.divIcon({
        html: `<div>${pointCount as number}</div>`,
        className: CLUSTER_ICON_CLASS,
        iconSize: L.point(40, 40),
      })

      const clusterMarker = L.marker([latitude, longitude], { icon: clusterIcon })

      clusterMarker.on('click', () => {
        map.setView([latitude, longitude], map.getZoom() + 1)
      })

      return clusterMarker
    }

    return markers[id]
  })
}

const useMapVisualizations = (map: Map) => {
  const { currentDatasetType } = useLegacyDataselectionConfig()
  const datasetType = currentDatasetType?.toUpperCase() as DataSelectionType
  const { showMapDrawVisualization } = useMapContext()
  const { buildQueryString } = useBuildQueryString()
  const navigate = useNavigate()
  const [visualizations, setVisualizations] = useState<Layer[]>([])

  const visualizationData = useVisualizationData(datasetType)

  useEffect(() => {
    if (!isFulfilled(visualizationData) || !showMapDrawVisualization || !visualizationData.value) {
      if (!showMapDrawVisualization) {
        setVisualizations([])
      }

      return () => undefined
    }

    if (visualizationData.value.type === DataSelectionMapVisualizationType.GeoJSON) {
      setVisualizations(
        visualizationData.value.data.map((feature) =>
          geoJSON(feature.geometry, {
            style: geoJsonConfig[feature.name]?.style,
          }),
        ),
      )
    } else if (visualizationData.value.type === DataSelectionMapVisualizationType.Markers) {
      const markers = getMarkers(visualizationData.value, navigate, buildQueryString, datasetType)
      const clusterIndex = getClusterIndex(markers)

      const updateClusters = () => {
        setVisualizations(getClustersOrMarkers(map, clusterIndex, markers))
      }

      updateClusters()
      map.on('moveend', updateClusters)

      return () => {
        map.off('moveend', updateClusters)
      }
    }

    setVisualizations([])
    return () => undefined
  }, [map, showMapDrawVisualization, visualizationData])

  useEffect(() => {
    visualizations.forEach((drawing) => drawing.addTo(map))

    return () => visualizations.forEach((drawing) => drawing.remove())
  }, [visualizations])
}

export default useMapVisualizations
